import { getCommendaAiConversation } from '@commenda/api/commenda-ai/api';
import { CommendaAiDocumentProcessingStage } from '@commenda/api/commenda-ai/types';
import { useCommendaAiDocumentProcessingStage } from '@commenda/api/commenda-ai/useCommendaAiDocumentProcessingStage';
import type { UIMessage } from 'ai';
import { useEffect, useRef, useState } from 'react';

/** Shows the "documents processed" message in an open chat. The backend writes it as an assistant
 * message once uploads finish, so this watches the processing stage and, on a move to idle, re-reads
 * the conversation and appends any assistant message not already on screen. It waits for the chat
 * to be idle, so the message never lands in the middle of a turn. */
export function useDocumentProcessingNotice({
  conversationId,
  isIdle,
  setMessages,
}: {
  conversationId: string;
  isIdle: boolean;
  setMessages: (update: (current: UIMessage[]) => UIMessage[]) => void;
}) {
  // A background read: while it loads or after it fails there is no stage, so no notice is shown.
  const { stage, mutate } =
    useCommendaAiDocumentProcessingStage(conversationId);

  const [pendingNoticeFor, setPendingNoticeFor] = useState<string | null>(null);
  const previousRef = useRef<{
    conversationId: string;
    stage: CommendaAiDocumentProcessingStage | undefined;
  } | null>(null);
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { conversationId, stage };
    if (
      previous?.conversationId === conversationId &&
      previous.stage &&
      previous.stage !== CommendaAiDocumentProcessingStage.IDLE &&
      stage === CommendaAiDocumentProcessingStage.IDLE
    )
      setPendingNoticeFor(conversationId);
  }, [conversationId, stage]);

  // A turn that just finished may have started an upload, so the stage is re-read when the chat goes idle.
  const wasIdleRef = useRef(isIdle);
  useEffect(() => {
    if (isIdle && !wasIdleRef.current) mutate().catch(() => undefined);
    wasIdleRef.current = isIdle;
  }, [isIdle, mutate]);

  useEffect(() => {
    if (pendingNoticeFor !== conversationId || !isIdle) return;
    setPendingNoticeFor(null);

    getCommendaAiConversation(conversationId)
      .then(({ messages }) => {
        if (conversationIdRef.current !== conversationId) return;
        setMessages((current) => {
          const shownIds = new Set(current.map((message) => message.id));
          const unseen = messages.filter(
            (message) =>
              message.role === 'assistant' && !shownIds.has(message.id)
          );
          return unseen.length ? [...current, ...unseen] : current;
        });
      })
      .catch(() => undefined);
  }, [pendingNoticeFor, isIdle, conversationId, setMessages]);
}
